import { Box, Button, Divider, Typography } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { ICore } from "../../../core/ICore";
import { useFormContext } from "../../../core/form/IForm";

/** The recommended configuration (same as the API's SaleUtility REACTIVE_DEFAULTS). */
export const REACTIVE_ADVANCED_DEFAULTS = {
  reactiveGap: 0.2,
  breakevenFloor: 0.2,
  lockGap1: 1.0,
  lockTier2From: 4,
  lockGap2: 0.8,
  lockTier3From: 7,
  lockGap3: 0.6,
  maxProfit: 10,
  lockGap4: 0.3,
};
export const REACTIVE_ADVANCED_FIELDS = Object.keys(REACTIVE_ADVANCED_DEFAULTS);

const Field = ({ model, label, helper }) => {
  const form = useFormContext();
  const error = form.touched[model] && form.errors[model];
  return (
    <ICore.ITextBox model={model} label={label} type="number" inputProps={{ step: "any", min: 0, inputMode: "decimal" }} helperText={error || helper} />
  );
};

/**
 * Advanced config for Reactive (same popover as the Buy engine's): trigger step, the lock gap by profit
 * tier, breakeven floor and max profit (tightens the lock - it doesn't sell). Fields live in the sale
 * engine form; autoOpen shows it when one of them fails validation on Start.
 */
const ReactiveConfig = ({ autoOpen, onAutoOpen }) => {
  const form = useFormContext();
  const v = form.values;
  const reset = () => form.setValues({ ...form.values, ...REACTIVE_ADVANCED_DEFAULTS });

  return (
    <ICore.IPopover icon={<TuneIcon />} title="Advanced config · Reactive" width={460} autoOpen={autoOpen} onAutoOpen={onAutoOpen}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Each time price reaches the trigger: locked exit = price − lock gap (never below buy + breakeven, never lower than before);
        next trigger = price + trigger step. Sells when price falls to the locked exit.
      </Typography>
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
        <Field model="reactiveGap" label="Trigger step %" helper="next trigger above price" />
        <Field model="breakevenFloor" label="Breakeven floor %" helper="lowest locked exit" />
      </Box>
      <Divider sx={{ my: 1.5 }}>
        <Typography variant="caption" color="text.secondary">Lock gap by profit from buy</Typography>
      </Divider>
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
        <Field model="lockGap1" label="Tier 1 lock gap %" helper={`trigger → ${v.lockTier2From}%`} />
        <Field model="lockTier2From" label="Tier 2 from profit %" />
        <Field model="lockGap2" label="Tier 2 lock gap %" helper={`${v.lockTier2From}% → ${v.lockTier3From}%`} />
        <Field model="lockTier3From" label="Tier 3 from profit %" />
        <Field model="lockGap3" label="Tier 3 lock gap %" helper={`${v.lockTier3From}% → ${v.maxProfit}%`} />
        <Field model="maxProfit" label="Max profit %" helper="tightens, doesn't sell" />
        <Field model="lockGap4" label="Above max profit lock gap %" helper={`from ${v.maxProfit}%`} />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
        <Button size="small" onClick={reset}>Reset to defaults</Button>
      </Box>
    </ICore.IPopover>
  );
};

export default ReactiveConfig;
